import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Clock, HelpCircle, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import { Btn, Card, CardBody, InputField } from '@/presentation/component/ui';
import { inputClass } from '@/presentation/component/formStyles';

export const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    toast.success(t('contact.messageSuccess'));
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const faqs = [
    { q: t('contact.faqQ1'), a: t('contact.faqA1') },
    { q: t('contact.faqQ2'), a: t('contact.faqA2') },
    { q: t('contact.faqQ3'), a: t('contact.faqA3') },
  ];

  return (
    <div className="min-h-screen py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold text-[var(--color-ink)]">{t('contact.title')}</h1>
          <p className="mx-auto max-w-2xl text-lg text-[var(--color-ink-soft)]">
            {t('contact.description')}
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Form */}
          <Card>
            <CardBody>
              <h2 className="mb-6 text-2xl font-semibold text-[var(--color-ink)]">
                {t('contact.sendMessageTitle')}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <InputField
                  label={t('contact.fullName')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.fullNamePlaceholder')}
                  required
                />
                <InputField
                  label={t('contact.email')}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact.emailPlaceholder')}
                  required
                />
                <InputField
                  label={t('contact.subject')}
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('contact.subjectPlaceholder')}
                  required
                />
                <div>
                  <label
                    htmlFor="contact-message"
                    className="mb-1 block text-sm font-medium text-[var(--color-ink)]"
                  >
                    {t('contact.message')}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    required
                    className={inputClass()}
                    placeholder={t('contact.messagePlaceholder')}
                  />
                </div>
                <Btn
                  type="submit"
                  kind="primary"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  icon={<Send className="h-4 w-4" />}
                  className="w-full"
                >
                  {t('buttons.sendMessage')}
                </Btn>
              </form>
            </CardBody>
          </Card>

          {/* Info */}
          <div className="space-y-6">
            {/* Quick support */}
            <div className="rounded-xl bg-[var(--color-accent-soft)] p-6">
              <h3 className="mb-4 text-lg font-semibold text-[var(--color-accent-ink)]">
                {t('contact.quickSupport')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[var(--color-accent)]" />
                  <span className="text-[var(--color-accent-ink)]">
                    {t('contact.supportEmail')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-[var(--color-accent)]" />
                  <span className="text-[var(--color-accent-ink)]">
                    {t('contact.supportPhone')}
                  </span>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="rounded-xl bg-[var(--color-surface-2)] p-6">
              <div className="mb-4 flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-[var(--color-ink-soft)]" />
                <h3 className="text-lg font-semibold text-[var(--color-ink)]">
                  {t('contact.faq')}
                </h3>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div key={i}>
                    <h4 className="font-medium text-[var(--color-ink)]">{faq.q}</h4>
                    <p className="mt-1 text-sm text-[var(--color-ink-soft)]">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Response time */}
            <div className="rounded-xl bg-[var(--color-good-bg)] p-6">
              <div className="mb-2 flex items-center gap-2">
                <Clock className="h-5 w-5 text-[var(--color-good)]" />
                <h3 className="text-lg font-semibold text-[var(--color-ink)]">
                  {t('contact.responseTime')}
                </h3>
              </div>
              <p className="text-sm text-[var(--color-ink-soft)]">
                {t('contact.responseTimeDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
